import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Modal, ModalDialog, ModalClose, Typography, Box, Button, Alert, CircularProgress } from "@mui/joy";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import { bookApi } from "../../api.js";
import { bookFormatLabel } from "../../utils/bookFormatLabel.js";
import { saveErrorText } from "../../utils/saveErrorText.js";

export default function ModalBookPages({ book, onClose, onChanged }) {
  const [fresh, setFresh] = useState(null);
  const [variants, setVariants] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmation, setConfirmation] = useState(null);
  const [reload, setReload] = useState(0);
  const lock = useRef(false);
  const reduced = useReducedMotion();
  const id = book?.id;

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true); setError(""); setFresh(null);
    setVariants([]); setConfirmation(null); setSelected(null);
    Promise.all([bookApi.getBook(id), bookApi.getBookFormats()]).then(([data, formats]) => {
      if (cancelled) return;
      const format = formats.find(f => String(f.id) === String(data.book_format));
      const choices = [...(format?.page_variants || [])]
        .filter(v => Number.isInteger(Number(v.pages)) && Number(v.pages) > 0)
        .sort((a, b) => Number(a.pages) - Number(b.pages));
      if (!format || !choices.length) {
        setError("Для формата этой книги пока нет доступных вариантов страниц. Обратитесь в поддержку.");
      }
      setFresh(data); setVariants(choices); setSelected(Number(data.pages_count));
    }).catch(err => { if (!cancelled) setError(saveErrorText(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id, reload]);

  const submit = async (allowShrink = false) => {
    if (lock.current || !fresh || !variants.some(v => Number(v.pages) === selected)) return;
    lock.current = true; setBusy(true); setError("");
    let changed = null;
    try {
      // Здесь нет book_format / width_mm / height_mm: меняется только объём.
      changed = await bookApi.resizeBook(id, {
        pagesCount: selected,
        expectedSpreads: fresh.spreads.length,
        allowShrink,
        shrinkToken: allowShrink ? confirmation?.shrink_token : undefined,
      });
    } catch (err) {
      if (err.status === 409 && err.data?.code === "shrink_confirmation_required") {
        setConfirmation(err.data);
      } else {
        setConfirmation(null);
        setError(err.data?.code === "book_changed"
          ? "Количество страниц уже изменилось. Нажмите «Проверить данные» и выберите объём заново."
          : saveErrorText(err));
      }
    }
    if (changed) {
      // Не повторяем POST, если обновление интерфейса завершится с ошибкой.
      try { await onChanged(changed); onClose(); }
      catch (_) { setError("Количество страниц сохранено. Закройте это окно и заново откройте книгу, чтобы увидеть результат."); setFresh(changed); setConfirmation(null); }
    }
    lock.current = false; setBusy(false);
  };
  const close = () => { if (!lock.current) onClose(); };
  return (
    <Modal open={!!id} onClose={close} sx={{ zIndex: 10030 }}>
      <ModalDialog aria-labelledby="book-pages-title"
        sx={{ width: "min(440px, calc(100vw - 24px))", maxHeight: "85dvh", overflowY: "auto", borderRadius: 20, p: 0 }}>
        <Box component={motion.div} initial={{ opacity: 0, y: reduced ? 0 : 12 }}
          animate={{ opacity: 1, y: 0 }} transition={{ duration: reduced ? 0 : 0.2 }} sx={{ p: 3 }}>
          <ModalClose disabled={busy} aria-label="Закрыть" />
          <Typography id="book-pages-title" level="title-lg" sx={{ pr: 3 }}>Количество страниц</Typography>
          <Typography level="body-sm" sx={{ mt: 1 }}>{bookFormatLabel(fresh || book)}</Typography>
          <Typography level="body-sm" sx={{ mb: 2 }}>Формат книги останется прежним.</Typography>
          {loading ? <CircularProgress size="sm" /> : (
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 1 }}>
              {variants.map(v => (
                <Button key={v.id ?? v.pages} variant={selected === Number(v.pages) ? "soft" : "outlined"}
                  color={selected === Number(v.pages) ? "primary" : "neutral"}
                  aria-pressed={selected === Number(v.pages)} disabled={busy}
                  onClick={() => { setSelected(Number(v.pages)); setConfirmation(null); setError(""); }}
                  sx={{ flexDirection: "column", py: 2, borderRadius: 14, gap: 0.5 }}>
                  <MenuBookRoundedIcon />
                  <span>{v.pages} страниц</span>
                  <Typography level="body-xs">{Number(v.price).toLocaleString("ru-RU")} ₽</Typography>
                  {Number(v.pages) === Number(fresh?.pages_count) && <Typography level="body-xs">Сейчас</Typography>}
                </Button>
              ))}
            </Box>
          )}
          {error && <Alert color="danger" sx={{ mt: 2 }}>{error}</Alert>}
          {confirmation && <Alert color="warning" sx={{ mt: 2 }}>
            Будут удалены последние {confirmation.pages_to_remove} страниц и фотографии на них
            ({confirmation.photos_to_remove} шт.). Отменить удаление в редакторе нельзя.
          </Alert>}
          {!confirmation && fresh && selected > Number(fresh.pages_count) &&
            <Typography level="body-sm" sx={{ mt: 2 }}>В конце книги появятся {selected - Number(fresh.pages_count)} новых пустых страниц.</Typography>}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2 }}>
            <Button variant="plain" color="neutral" disabled={busy} onClick={close}>Отмена</Button>
            {error && <Button variant="outlined" disabled={busy} onClick={() => setReload(n => n + 1)}>Проверить данные</Button>}
            <Button loading={busy} disabled={loading || !fresh || !variants.length || !!error || selected === Number(fresh?.pages_count)}
              color={confirmation ? "danger" : "primary"} onClick={() => submit(!!confirmation)} sx={{ flex: 1 }}>
              {confirmation ? "Удалить лишние страницы" : "Применить"}
            </Button>
          </Box>
        </Box>
      </ModalDialog>
    </Modal>
  );
}